import { useId, useState } from "react";
import {
  Button,
  Dialog,
  DialogFooter,
  DialogText,
  DrawerSection,
  FailedCallout,
  FieldError,
  FieldLabel,
  Tag,
  TextArea,
  TextField,
  formatDate,
  formatDateTime,
  isApiError,
  useCommandMutation,
  useToast,
} from "@amber/ui";
import type { TaskDetail } from "../../contracts/index";
import { operationsCommands } from "../queries";
import { AFTER_OPERATIONS_CHANGE } from "../keys";

const FACTS = "m-0 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-md [&_dd]:m-0 [&_dt]:text-muted";

export function SeverityTag({ severity }: { severity: TaskDetail["severity"] }) {
  if (!severity) return null;
  return (
    <Tag tone="strong">
      {severity.code} · {severity.label}
    </Tag>
  );
}

function windowText(window: NonNullable<TaskDetail["coverage_window"]>): string {
  return `${formatDate(window.from)} – ${window.to ? formatDate(window.to) : "ongoing"}`;
}

export function IncidentFacts({ detail }: { detail: TaskDetail }) {
  if (!detail.severity && !detail.coverage_window) return null;
  return (
    <dl className={`${FACTS} mt-2`}>
      {detail.severity ? (
        <>
          <dt>Severity</dt>
          <dd>
            {detail.severity.code}: {detail.severity.meaning}
          </dd>
          <dt>Containment</dt>
          <dd>{detail.severity.containment}</dd>
        </>
      ) : null}
      {detail.coverage_window ? (
        <>
          <dt>Coverage window</dt>
          <dd>{windowText(detail.coverage_window)}</dd>
        </>
      ) : null}
    </dl>
  );
}

export function RecoverySection({ detail, onRecord }: { detail: TaskDetail; onRecord: () => void }) {
  const availability = detail.record_recovery;
  if (!availability) return null;
  const recovery = detail.recovery;
  return (
    <DrawerSection title="Recovery" {...(recovery ? { note: `recorded ${formatDateTime(recovery.at)}` } : {})}>
      {recovery ? (
        <dl className={FACTS}>
          <dt>Evidence</dt>
          <dd className="break-words whitespace-pre-wrap">{recovery.evidence}</dd>
          <dt>Follow-up</dt>
          <dd>{recovery.follow_up ?? "None"}</dd>
          <dt>By</dt>
          <dd>{recovery.by}</dd>
        </dl>
      ) : (
        <p className="m-0 text-md text-muted">No recovery evidence recorded yet.</p>
      )}
      <div className="mt-2.5">
        <Button disabledReason={availability.enabled ? null : availability.reason} onClick={onRecord}>
          Record recovery…
        </Button>
      </div>
    </DrawerSection>
  );
}

const EVIDENCE_REQUIRED = "Describe what recovered and how you know";

export function RecordRecoveryDialog({ detail, onClose }: { detail: TaskDetail; onClose: () => void }) {
  const toast = useToast();
  const id = useId();
  const [evidence, setEvidence] = useState(detail.recovery?.evidence ?? "");
  const [followUp, setFollowUp] = useState(detail.recovery?.follow_up ?? "");
  const [missing, setMissing] = useState(false);
  const mutation = useCommandMutation({
    run: (csrf, body: { evidence: string; follow_up: string }) =>
      operationsCommands.recordRecovery(detail.task.id, body, csrf),
    invalidate: AFTER_OPERATIONS_CHANGE,
    onSuccess: () => {
      toast.show("Recovery recorded");
      onClose();
    },
  });
  const serverError = isApiError(mutation.error) ? (mutation.error.fieldErrors()["evidence"] ?? null) : null;
  const error = missing ? EVIDENCE_REQUIRED : serverError;
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title="Record recovery"
      onSubmit={() => {
        const trimmed = evidence.trim();
        setMissing(trimmed.length === 0);
        if (trimmed.length > 0) mutation.mutate({ evidence: trimmed, follow_up: followUp.trim() });
      }}
      footer={
        <DialogFooter onClose={onClose} confirm="Record" pending={mutation.isPending} failed={mutation.isError} />
      }
    >
      <DialogText>
        <b>{detail.task.title}</b> · {detail.task.context}
      </DialogText>
      <DialogText>
        What recovered, when, and how you checked it. Recorded in the task's history and the audit log.
      </DialogText>
      <div className="mt-3">
        <FieldLabel htmlFor={id} required>
          Recovery evidence
        </FieldLabel>
        <TextArea
          id={id}
          value={evidence}
          placeholder="For example the scrub's JSON summary, or “Released the route after its test request”"
          aria-required="true"
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${id}-err` : undefined}
          onChange={(e) => {
            setEvidence(e.target.value);
          }}
        />
        {error ? <FieldError id={`${id}-err`}>{error}</FieldError> : null}
      </div>
      <TextField
        label="Follow-up action"
        optional="optional"
        value={followUp}
        onChange={(e) => {
          setFollowUp(e.target.value);
        }}
      />
      <FailedCallout error={mutation.error} fieldsShown />
    </Dialog>
  );
}
